import { css, cx } from "@emotion/css";
import { MenuItem, useForkRef } from "@material-ui/core";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ShowLinkAsActive } from "@octopusdeploy/routing";
import { useIsUrlActive, useOctopusLinkComponent } from "@octopusdeploy/routing";
import type { PropsWithChildren, Ref } from "react";
import React, { forwardRef, useEffect, useRef } from "react";
export interface MenuItemInternalLinkProps {
    path: string;
    label: string;
    shortDescription?: string;
    icon?: JSX.Element;
    showLinkAsActive?: ShowLinkAsActive;
    onClick?: () => void;
    autoFocus?: boolean;
    size?: LinkSize;
}
export const MenuItemInternalLink = forwardRef(({ path, label, onClick, autoFocus, size = "default", icon, shortDescription, showLinkAsActive = "never" }: MenuItemInternalLinkProps, ref: Ref<HTMLAnchorElement>) => {
    // We can't use the default li component that MenuItem renders here, because the parent element (CustomMenu or MenuList) is not a ul
    return <MenuItem component={LinkWrapper} path={path} label={label} onClick={onClick} showLinkAsActive={showLinkAsActive} autoFocus={autoFocus} size={size} ref={ref} icon={icon} shortDescription={shortDescription}/>;
});
MenuItemInternalLink.displayName = "MenuItemInternalLink";
interface LinkWrapperProps {
    path: string;
    icon: JSX.Element | undefined;
    onClick: undefined | (() => void);
    label: string;
    shortDescription: string | undefined;
    showLinkAsActive: ShowLinkAsActive;
    className: string;
    autoFocus: boolean | undefined;
    size: LinkSize;
}
const LinkWrapper = forwardRef(({ path, showLinkAsActive, label, onClick, className, children, autoFocus, size, icon, shortDescription, ...other }: PropsWithChildren<LinkWrapperProps>, ref: Ref<HTMLAnchorElement>) => {
    const Link = useOctopusLinkComponent();
    const isUrlActive = useIsUrlActive();
    const isActive = showLinkAsActive === "never" ? false : isUrlActive(path, showLinkAsActive === "if path matches exactly");
    const sizeClass = size === "compact" ? compactStyles : size === "default" ? defaultSizeStyles : spaciousStyles;
    const classes = cx(className, linkStyles, sizeClass);
    const focusRef = useRef<HTMLAnchorElement | null>(null);
    const combinedRef = useForkRef(focusRef, ref);
    useEffect(() => {
        if (autoFocus) {
            focusRef.current?.focus();
        }
    }, [autoFocus]);
    // Ideally we would wrap an anchor tag in an li like in this example: https://www.w3.org/TR/wai-aria-practices/examples/menu-button/menu-button-links.html
    // <li role="none"><a role="menuitem">My Link</a></li>
    // But if we do that, we lose out on keyboard navigation support from material ui's MenuList component
    // Instead, we want to render an anchor tag so that we get the right native browser link behaviours,
    // but make it appear like a menuitem for the accessibility tree
    return (
    // eslint-disable-next-line react/forbid-elements
    <Link aria-disabled={undefined} role={"menuItem"} aria-label={label} href={path} aria-current={isActive ? "page" : undefined} onClick={onClick} tabIndex={-1} className={classes} ref={combinedRef} {...other}>
            {icon && <div className={iconStyles}>{icon}</div>}
            <div className={textContainerStyles}>
                <div className={labelStyles}>{label}</div>
                {shortDescription && <div className={descriptionStyles}>{shortDescription}</div>}
            </div>
            {/*The children is the ripple effect*/}
            {children}
        </Link>);
});
LinkWrapper.displayName = "LinkWrapper";
export type LinkSize = "default" | "compact" | "spacious";
const linkStyles = css({
    "&.MuiMenuItem-root": {
        display: "flex",
        alignItems: "center",
        "&:hover": {
            color: "inherit",
        },
        "&.MuiListItem-button": {
            "&:hover": {
                backgroundColor: themeTokens.color.mock.menu.hover,
            },
            "&:focus": {
                backgroundColor: themeTokens.color.mock.menu.hover,
            },
        },
        "&[aria-current=page]": {
            color: themeTokens.color.mock.menu.primary,
        },
    },
});
const compactStyles = css({
    "&.MuiMenuItem-root": {
        minHeight: "auto",
    },
});
const defaultSizeStyles = css({
    "&.MuiMenuItem-root": {
        minHeight: space[8],
    },
});
const spaciousStyles = css({
    "&.MuiMenuItem-root": {
        minHeight: space[9],
        paddingTop: space[4],
        paddingBottom: space[4],
    },
});
const iconStyles = css({
    paddingRight: space[3],
});
const labelStyles = css({
    wordBreak: "break-all",
    whiteSpace: "normal",
});
const textContainerStyles = css({
    display: "flex",
    flexDirection: "column",
});
const descriptionStyles = css({
    font: text.navigation.link.medium,
    wordBreak: "break-all",
    whiteSpace: "normal",
    color: themeTokens.color.mock.menu.ternaryText,
});
